import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  IWHChecklistDefinitionDTO,
  WHIconENUM,
} from '@workheld/workheld-shared-lib';
import { firstValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/app-dialogs/app-dialog.service';
import { AppSharedModule } from 'src/app/app-shared/app-shared.module';
import { WHButtonComponent } from 'src/app/app-shared/components/buttons/w-h-button/w-h-button.component';
import { ChecklistDefinitionAsyncService } from 'src/app/app-services-async/checklist-definition-async.service';
import { IAppStore } from 'src/app/app-stores/index.stores';
import { workheldChecklistDefinitionCrudStoreFeatureKey } from 'src/app/app-stores/w-h-checklist-definition-stores/w-h-checklist-definition-crud-store/w-h-checklist-definition-crud-store.reducer';
import { ChecklistTableComponent } from '../checklist-table/checklist-table.component';

export type ChecklistDefinitionEditMode = 'update' | 'duplicate';

@Component({
  selector: 'ng-bee-checklist-definition-detail',
  imports: [AppSharedModule, WHButtonComponent, ChecklistTableComponent],
  templateUrl: './checklist-definition-detail.component.html',
  styleUrl: './checklist-definition-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChecklistDefinitionDetailComponent {
  public readonly displayHeader = input(true);
  public readonly displayHeaderInBody = input(false);
  public readonly displayActionButtons = input(true);

  public readonly editRequested = output<ChecklistDefinitionEditMode>();

  private readonly store = inject<Store<IAppStore>>(Store);
  private readonly checklistDefinitionAsyncService = inject(
    ChecklistDefinitionAsyncService,
  );
  private readonly appDialogService = inject(AppDialogService);

  protected readonly checklistDefinitionIcon =
    WHIconENUM.ChecklistDefinitionIcon as string;

  protected readonly checklistDefinition = this.store.selectSignal(
    (state: IAppStore) => state[workheldChecklistDefinitionCrudStoreFeatureKey],
  );
  protected readonly status = linkedSignal(
    () => this.checklistDefinition()?.status,
  );
  protected readonly isActive = computed(() => this.status() === 'ACTIVE');
  protected readonly statusUpdating = signal(false);

  private get displayName(): string {
    const checklistDefinition = this.checklistDefinition();
    return `${checklistDefinition?.extId} - ${checklistDefinition?.title}`;
  }

  protected editChecklistDefinition(): void {
    this.editRequested.emit('update');
  }

  protected async copyChecklistDefinition(): Promise<void> {
    const confirmed = await firstValueFrom(
      this.appDialogService
        .openConfirmMatDialog({
          header: 'checklistdefinition.ui.duplicate.label',
          body: 'confirm.duplicate.body',
          bodyParams: { object: this.displayName },
          alertType: 'info',
          alert: 'confirmationdialog.duplicatechecklistdefinition.body.label',
          submitAction: 'checklistdefinition.ui.duplicate.label',
        })
        .afterClosed(),
    );
    if (confirmed) {
      this.editRequested.emit('duplicate');
    }
  }

  protected async toggleStatus(): Promise<void> {
    const checklistDefinitionId = this.checklistDefinition()?.id;
    const deactivate = this.isActive();

    const confirmed = await firstValueFrom(
      this.appDialogService
        .openConfirmMatDialog(
          deactivate
            ? {
                header: 'app.deactivate.label',
                body: 'confirm.deactivate.body',
                bodyParams: { object: this.displayName },
                submitAction: 'app.deactivate.label',
                alertType: 'info',
                alert:
                  'confirmationdialog.deactivatechecklistdefinition.body.label',
              }
            : {
                header: 'app.activate.label',
                body: 'confirm.activate.body',
                bodyParams: { object: this.displayName },
                submitAction: 'app.activate.label',
              },
        )
        .afterClosed(),
    );
    if (!confirmed || !checklistDefinitionId) {
      return;
    }

    this.statusUpdating.set(true);
    try {
      if (deactivate) {
        await firstValueFrom(
          this.checklistDefinitionAsyncService.deleteChecklistDefinitionByChecklistDefinitionId(
            checklistDefinitionId,
          ),
        );
        this.status.set('INACTIVE');
        this.backToOverview();
      } else {
        await firstValueFrom(
          this.checklistDefinitionAsyncService.updateChecklistDefinitionByChecklistDefinitionDTO(
            checklistDefinitionId,
            { status: 'ACTIVE' } as IWHChecklistDefinitionDTO,
          ),
        );
        this.status.set('ACTIVE');
      }
    } finally {
      this.statusUpdating.set(false);
    }
  }

  public backToOverview(): void {
    window.history.back();
  }
}
